import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RecitationSession } from '../services/recitationAPI';
import '../styles/RecitationAnswer.css';

const STATUS_LABELS = {
  idle: 'Prêt à écouter',
  connecting: 'Connexion au service de transcription…',
  recording: 'Écoute en cours…',
  stopping: 'Analyse de la réponse…',
  completed: 'Réponse analysée',
  error: 'Service indisponible',
};

// Précision minimale (mots corrects / mots évalués) pour valider une question.
const PASS_ACCURACY = 0.9;

const WORD_STATUS_LABELS = {
  correct: 'Correct',
  uncertain: 'À confirmer',
  incorrect: 'Incorrect',
  missing: 'Manquant',
  pending: 'Pas encore récité',
};

// Le backend expose désormais une définition unique : « évalué » = correct +
// incorrect + manquant. Les mots encore `pending` ou `uncertain` restent hors
// du dénominateur au lieu d'être comptés comme des erreurs.
function summarizeCompleted(completed, target) {
  const correctCount = completed.reduce((total, item) => total + Number(item.correct_count || 0), 0);
  const errorCount = completed.reduce((total, item) => total + Number(item.error_count || 0), 0);
  const evaluated = completed.reduce(
    (total, item) => total + Number(item.evaluated_count ?? (Number(item.correct_count || 0) + Number(item.error_count || 0))),
    0
  );
  const accuracy = evaluated ? correctCount / evaluated : 0;
  return {
    completedCount: completed.length,
    correctCount,
    errorCount,
    accuracy,
    reachedTarget: completed.length >= target,
    passed: completed.length >= target && accuracy >= PASS_ACCURACY,
  };
}

// Masque d'un mot non encore acquis. Identique à celui de VerseDisplay.
const PENDING_MASK = '•••';

// Infobulle d'un mot. On y décrit l'écart SANS jamais donner la forme
// attendue : révéler `expected` reviendrait à souffler la réponse au moment
// exact de l'erreur. Seul ce que le récitant a prononcé est restitué.
function describeWord(word, label) {
  const parts = [label];
  if (word.wrong_phonemes > 0) {
    parts.push(`${word.wrong_phonemes} phonème${word.wrong_phonemes > 1 ? 's' : ''} incorrect${word.wrong_phonemes > 1 ? 's' : ''}`);
  }
  if (word.heard) {
    parts.push(`vous avez prononcé « ${word.heard} »`);
  }
  return parts.join(' — ');
}

const RecitationAnswer = ({
  verse,
  versesTarget = 1,
  startAfterVerse = false,
  onProgress,
  onCompleted,
}) => {
  const sessionRef = useRef(null);
  const completedRef = useRef([]);
  const completionReportedRef = useRef(false);
  // Renseigné par le backend : 'manual', 'silence', ou null. Permet à l'appelant
  // de distinguer un abandon par silence d'un arrêt volontaire.
  const stopReasonRef = useRef(null);
  const targetRef = useRef(versesTarget);
  // Index des mots déjà récités CORRECTEMENT pour l'ayah en cours.
  //
  // C'est la mémoire de la progression : elle survit à un arrêt, ce qui permet
  // de reprendre là où l'on s'était interrompu au lieu de tout recommencer.
  // Un mot n'y entre jamais s'il a été mal récité — sinon l'application
  // donnerait la réponse au moment précis de l'erreur.
  const revealedRef = useRef(new Set());
  const revealedKeyRef = useRef(null);
  const [revealed, setRevealed] = useState(() => new Set());
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [completedAyahs, setCompletedAyahs] = useState([]);
  const [currentReference, setCurrentReference] = useState(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  const target = Math.max(1, Number(versesTarget) || 1);
  targetRef.current = target;
  const isRunning = ['connecting', 'recording', 'stopping'].includes(status);
  const hasProgress = revealed.size > 0;
  const summary = useMemo(
    () => summarizeCompleted(completedAyahs, target),
    [completedAyahs, target]
  );

  // Totaux de session : versets terminés + verset en cours. Sans ce cumul, la
  // grille affichait les compteurs du seul verset courant à côté d'un nombre
  // de versets qui, lui, couvrait toute la session.
  const totals = useMemo(() => ({
    correct: summary.correctCount + Number(result?.correct_count || 0),
    errors: summary.errorCount + Number(result?.error_count || 0),
  }), [summary, result]);

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
      sessionRef.current = null;
    };
  }, [verse?.surah, verse?.numberInSurah]);

  const registerCompletedAyah = (completedAyah) => {
    if (!completedAyah?.reference) return false;
    if (completedRef.current.length >= targetRef.current) return true;
    const key = `${completedAyah.reference.surah}:${completedAyah.reference.ayah}`;
    if (completedRef.current.some((item) => item.key === key)) {
      return completedRef.current.length >= targetRef.current;
    }

    completedRef.current = [...completedRef.current, { ...completedAyah, key }];
    setCompletedAyahs(completedRef.current);
    return completedRef.current.length >= targetRef.current;
  };

  const reportCompletion = () => {
    if (completionReportedRef.current) return;
    completionReportedRef.current = true;
    onCompleted?.({
      ...summarizeCompleted(completedRef.current, targetRef.current),
      stopReason: stopReasonRef.current,
    });
  };

  // Accumule les mots correctement récités de l'ayah courante. Le compteur est
  // remis à zéro dès que la session change d'ayah, sinon la progression du
  // verset précédent dévoilerait le suivant.
  const rememberRevealed = (payload) => {
    const reference = payload.reference;
    const key = reference ? `${reference.surah}:${reference.ayah}` : null;
    if (key !== revealedKeyRef.current) {
      revealedKeyRef.current = key;
      revealedRef.current = new Set();
    }
    let changed = false;
    for (const word of payload.words || []) {
      const wordStatus = word.display_status || word.status;
      if (wordStatus === 'correct' && !revealedRef.current.has(word.index)) {
        revealedRef.current.add(word.index);
        changed = true;
      }
    }
    if (changed) setRevealed(new Set(revealedRef.current));
  };

  const handleMessage = (payload) => {
    // La progression doit être à jour AVANT d'être transmise : l'affichage du
    // verset a besoin de ce qui est acquis, pas du seul dernier alignement.
    if (payload.type === 'result') {
      rememberRevealed(payload);
      onProgress?.({ ...payload, revealed: Array.from(revealedRef.current) });
    } else {
      onProgress?.(payload);
    }
    if (payload.type === 'ready') {
      setStatus('connecting');
      return;
    }
    if (payload.type === 'started') {
      setStatus('recording');
      setWarning(null);
      setCurrentReference(payload.reference || null);
      return;
    }
    if (payload.type === 'warning') {
      if (payload.code === 'silence_stop') {
        stopReasonRef.current = 'silence';
      }
      setWarning(payload.message || 'La récitation semble interrompue.');
      return;
    }
    if (payload.type === 'status' && payload.code === 'voice_resumed') {
      setWarning(null);
      return;
    }
    if (payload.type === 'result') {
      setResult(payload);
      setCurrentReference(payload.reference || null);
      if (payload.stop_reason) {
        stopReasonRef.current = payload.stop_reason;
      }
      let reachedTarget = false;
      if (payload.completed_ayah) {
        reachedTarget = registerCompletedAyah(payload.completed_ayah);
      }
      if (payload.session_complete || reachedTarget) {
        setStatus('completed');
        setWarning(null);
        reportCompletion();
        sessionRef.current?.stop('target_reached');
        return;
      }
      if (payload.final) {
        setStatus('completed');
        setWarning(null);
        reportCompletion();
      }
      return;
    }
    if (payload.type === 'error') {
      setStatus('error');
      setError(payload.message || 'Le service de transcription a rencontré une erreur.');
    }
  };

  const handleError = (sessionError) => {
    setStatus('error');
    setError(sessionError.message || 'Impossible de contacter le service de transcription.');
  };

  const handleClose = () => {
    sessionRef.current = null;
    setStatus((currentStatus) => (
      ['completed', 'error'].includes(currentStatus) ? currentStatus : 'idle'
    ));
  };

  // `resume` conserve les mots déjà acquis : on repart de là où l'on s'est
  // interrompu au lieu de remettre le verset à zéro.
  const startSession = async ({ resume = false } = {}) => {
    if (!verse?.surah || !verse?.numberInSurah) return;

    setError(null);
    setWarning(null);
    setResult(null);
    setCurrentReference(null);
    setStatus('connecting');
    completionReportedRef.current = false;
    stopReasonRef.current = null;
    if (!resume) {
      setCompletedAyahs([]);
      completedRef.current = [];
      revealedRef.current = new Set();
      revealedKeyRef.current = null;
      setRevealed(new Set());
    }

    const session = new RecitationSession({
      surah: verse.surah,
      ayah: verse.numberInSurah,
      continuous: true,
      maxAyahs: targetRef.current,
      startAfter: startAfterVerse,
      onMessage: handleMessage,
      onError: handleError,
      onClose: handleClose,
    });
    sessionRef.current = session;

    try {
      await session.start();
    } catch (sessionError) {
      sessionRef.current = null;
      setStatus('error');
      setError(sessionError.message || 'Impossible de démarrer la transcription.');
    }
  };

  const stopSession = () => {
    setStatus('stopping');
    sessionRef.current?.stop('manual');
  };

  const resetSession = () => {
    sessionRef.current?.destroy();
    sessionRef.current = null;
    completedRef.current = [];
    completionReportedRef.current = false;
    stopReasonRef.current = null;
    revealedRef.current = new Set();
    revealedKeyRef.current = null;
    setRevealed(new Set());
    setStatus('idle');
    setResult(null);
    setCompletedAyahs([]);
    setCurrentReference(null);
    setError(null);
    setWarning(null);
  };

  useEffect(() => {
    resetSession();
  }, [verse?.surah, verse?.numberInSurah]);

  const currentReferenceLabel = currentReference
    ? `Sourate ${currentReference.surah}, verset ${currentReference.ayah}`
    : null;

  return (
    <section className="recitation-answer" aria-label="Réponse vocale avec transcription">
      <div className="recitation-head">
        <div>
          <h3 className="recitation-title">Répondre à l’oral avec l’IA</h3>
          <p className="recitation-help">
            {startAfterVerse
              ? 'Retrouvez et récitez le verset qui suit celui affiché : '
              : 'Récitez à partir du verset affiché : '}
            l’IA enchaîne automatiquement les versets suivants,
            puis valide la question après {targetRef.current} verset{targetRef.current > 1 ? 's' : ''} récité{targetRef.current > 1 ? 's' : ''}.
          </p>
        </div>
        <span className={`recitation-status status-${status}`} role="status">
          {STATUS_LABELS[status]}
        </span>
      </div>

      <div className="recitation-actions">
        {!isRunning && status !== 'completed' && (
          <button type="button" className="btn btn-ai" onClick={() => startSession()}>
            {status === 'error' ? 'Réessayer la réponse' : 'Démarrer ma réponse'}
          </button>
        )}
        {isRunning && (
          <button type="button" className="btn btn-danger" onClick={stopSession} disabled={status === 'stopping'}>
            {status === 'stopping' ? 'Analyse…' : 'Arrêter et analyser'}
          </button>
        )}
        {/* Reprendre conserve les mots déjà acquis ; Recommencer remet à zéro. */}
        {!isRunning && hasProgress && (
          <button type="button" className="btn btn-ai" onClick={() => startSession({ resume: true })}>
            Reprendre où je me suis arrêté
          </button>
        )}
        {(status === 'completed' || hasProgress) && !isRunning && (
          <button type="button" className="btn btn-outline" onClick={resetSession}>
            Recommencer
          </button>
        )}
        <span className="recitation-target">
          Versets récités : {summary.completedCount} / {targetRef.current}
        </span>
      </div>

      {error && <p className="recitation-error" role="alert">{error}</p>}
      {warning && <p className="recitation-warning" role="status">{warning}</p>}

      {isRunning && currentReferenceLabel && (
        <p className="recitation-current">Verset en cours : {currentReferenceLabel}</p>
      )}

      {completedAyahs.length > 0 && (
        <ul className="recitation-completed" aria-label="Versets récités">
          {completedAyahs.map((item) => {
            const evaluated = Number(item.correct_count || 0) + Number(item.error_count || 0);
            const ayahAccuracy = evaluated ? Number(item.correct_count || 0) / evaluated : 0;
            const ok = ayahAccuracy >= PASS_ACCURACY;
            return (
              <li key={item.key} className={ok ? 'completed-ok' : 'completed-warn'}>
                <span>{item.reference.surah}:{item.reference.ayah}</span>
                <span>{item.correct_count || 0} corrects · {item.error_count || 0} erreurs</span>
                <span>{ok ? '✓' : '✗'}</span>
              </li>
            );
          })}
        </ul>
      )}

      {status === 'completed' && (
        <p className={`recitation-verdict ${summary.passed ? 'verdict-passed' : 'verdict-review'}`}>
          {summary.passed
            ? `Objectif atteint : ${summary.completedCount} verset${summary.completedCount > 1 ? 's' : ''} récité${summary.completedCount > 1 ? 's' : ''} avec ${Math.round(summary.accuracy * 100)}% de précision.`
            : summary.reachedTarget
              ? `Objectif atteint mais précision insuffisante (${Math.round(summary.accuracy * 100)}%) : question à revoir.`
              : `Seulement ${summary.completedCount} verset${summary.completedCount > 1 ? 's' : ''} sur ${targetRef.current} : question à revoir.`}
        </p>
      )}

      {result && (
        <div className="recitation-result">
          {/* La grille mélangeait les périmètres : « versets récités » couvrait
              la session tandis que « mots corrects » et « erreurs » ne
              portaient que sur le verset en cours. Les totaux affichés
              contredisaient donc la liste des versets terminés juste au-dessus.
              Tout est désormais cumulé sur la session, sauf la progression,
              explicitement rattachée au verset courant. */}
          <div className="recitation-score-grid">
            <div><strong>{summary.completedCount}</strong><span>versets récités</span></div>
            <div><strong>{totals.correct}</strong><span>mots corrects</span></div>
            <div><strong>{totals.errors}</strong><span>erreurs</span></div>
            <div><strong>{Math.round(Number(result.progress || 0) * 100)}%</strong><span>progression du verset en cours</span></div>
          </div>
          {/* Le texte de référence renvoyé par l'API n'est JAMAIS affiché tel
              quel : ni les mots restants, ni la forme attendue d'un mot fauté.
              Seule la sortie du modèle — ce que le récitant a réellement
              prononcé — est montrée. */}
          {/* Repliée par défaut : la suite brute de phonèmes d'un verset entier
              formait un pavé illisible qui écrasait la correction mot à mot. */}
          {result.phoneme_transcript && (
            <details className="recitation-heard">
              <summary>Entendu par l’IA (phonèmes bruts)</summary>
              <p className="heard-phonemes" dir="rtl" lang="ar">{result.phoneme_transcript}</p>
              <p className="heard-note">
                Le modèle produit des phonèmes, pas des mots. Cette suite ne couvre
                que le verset en cours.
              </p>
            </details>
          )}
          {Array.isArray(result.words) && result.words.length > 0 && (
            <>
              <div className="recitation-words" aria-label="Correction mot par mot">
                {result.words.map((word) => {
                  const displayStatus = word.display_status || word.status;
                  const label = WORD_STATUS_LABELS[displayStatus] || displayStatus;
                  // Un mot ne se dévoile qu'une fois récité CORRECTEMENT, et le
                  // reste ensuite. Une erreur laisse donc le mot masqué : sans
                  // cela l'application donnerait la réponse au moment de la faute.
                  const isRevealed = revealed.has(word.index);
                  return (
                    <span
                      key={`${word.index}-${word.text}`}
                      className={`recitation-word word-${displayStatus}${isRevealed ? '' : ' word-hidden'}`}
                      title={describeWord(word, label)}
                      aria-label={isRevealed ? `${word.text} : ${label}` : label}
                    >
                      {isRevealed ? word.text : PENDING_MASK}
                    </span>
                  );
                })}
              </div>
              <div className="recitation-legend" aria-label="Légende des couleurs">
                <span><i className="legend-dot legend-correct" />Récité correctement</span>
                <span><i className="legend-dot legend-uncertain" />À confirmer</span>
                <span><i className="legend-dot legend-incorrect" />Erreur</span>
                <span><i className="legend-dot legend-pending" />Pas encore récité</span>
              </div>
              {/* Mention imposée par la licence du modèle acoustique (NPL-1.1),
                  qui exige de dire clairement, dans toute application bâtie
                  dessus, que la correction automatique peut se tromper et ne
                  remplace pas un enseignant. Elle est placée au contact du
                  verdict, et non reléguée dans une page « à propos ». */}
              <p className="recitation-disclaimer">
                Cette correction est automatique : elle peut se tromper et ne
                remplace pas l’écoute d’un enseignant qualifié. Elle ne constitue
                pas un avis religieux sur votre récitation.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default RecitationAnswer;
